<!-- eslint-disable vue/valid-v-slot -->
<template>
  <div class="payout-config-page">
    <div class="page-header">
      <div class="page-header-text">
        <h1 class="page-title">Konfiguracja wypłat</h1>
      </div>
    </div>
    <v-card class="content-card">
      <v-card-text>
          <v-form ref="form" v-model="valid">
            <!-- ZUS Rates Section -->
            <v-card outlined class="mb-4">
              <v-card-title class="subtitle-1">
                <v-icon left small>mdi-account-cash</v-icon>
                {{ $t('payoutConfig.zusRatesByType') }}
              </v-card-title>
              <v-card-text>
                <v-expansion-panels accordion>
                  <v-expansion-panel v-for="zusType in zusTypes" :key="zusType.key">
                    <v-expansion-panel-header>
                      <v-row no-gutters align="center">
                        <v-col cols="6">
                          <span class="font-weight-medium">{{ zusType.label }}</span>
                          <span class="text-caption grey--text ml-2">({{ zusType.key }})</span>
                        </v-col>
                        <v-col cols="6" class="text-right pr-4">
                          <span class="font-weight-bold primary--text">
                            {{ $t('payoutConfig.total') }}: {{ getZusTotal(zusType.key).toFixed(2) }} PLN
                          </span>
                        </v-col>
                      </v-row>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <v-row>
                        <v-col cols="12" md="3">
                          <v-text-field
                            v-model.number="zusConfigs[zusType.key].chorobowe"
                            :label="$t('payoutConfig.sickLeave')"
                            type="number"
                            outlined
                            dense
                            prefix="PLN"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="3">
                          <v-text-field
                            v-model.number="zusConfigs[zusType.key].spoleczne"
                            :label="$t('payoutConfig.social')"
                            type="number"
                            outlined
                            dense
                            prefix="PLN"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="3">
                          <v-text-field
                            v-model.number="zusConfigs[zusType.key].funduszPracy"
                            :label="$t('payoutConfig.pitAdvance')"
                            type="number"
                            outlined
                            dense
                            prefix="PLN"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="3">
                          <v-text-field
                            v-model.number="zusConfigs[zusType.key].zdrowotne"
                            :label="$t('payoutConfig.health')"
                            type="number"
                            outlined
                            dense
                            prefix="PLN"
                          ></v-text-field>
                        </v-col>
                      </v-row>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-card-text>
            </v-card>

            <!-- Próg rozliczenia Section -->
            <v-card outlined class="mb-4">
              <v-card-title class="subtitle-1">
                <v-icon left small>mdi-file-document-outline</v-icon>
                {{ $t('payoutConfig.settlementThreshold') }}
              </v-card-title>
              <v-card-text>
                <v-row>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model.number="config.minSettlementAmount"
                      :label="$t('payoutConfig.minSettlementAmount')"
                      type="number"
                      outlined
                      dense
                      prefix="PLN"
                      :hint="$t('payoutConfig.minSettlementHint')"
                      persistent-hint
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model.number="config.komornikFee"
                      label="Opłata za pismo komornicze"
                      type="number"
                      outlined
                      dense
                      prefix="PLN"
                      hint="Naliczana za każde dodane pismo komornicze"
                      persistent-hint
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model.number="config.officeInvoiceFee"
                      label="Opłata za dodanie faktury przez biuro"
                      type="number"
                      outlined
                      dense
                      prefix="PLN"
                      hint="Gdy kierowca nie wrzuca faktury sam, tylko prosi biuro (0 = wyłączone)"
                      persistent-hint
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-simple-table dense class="mt-4">
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th>{{ $t('payoutConfig.zusType') }}</th>
                        <th>{{ $t('payoutConfig.zusTotal') }}</th>
                        <th>{{ $t('payoutConfig.suggested') }}</th>
                        <th>{{ $t('payoutConfig.maxContract') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="zusType in zusTypes" :key="'zlec-' + zusType.key">
                        <td>{{ zusType.label }}</td>
                        <td>{{ getZusTotal(zusType.key).toFixed(2) }} PLN</td>
                        <td class="grey--text">{{ getSuggestedZleceniowka(zusType.key).toFixed(2) }} PLN</td>
                        <td style="width: 200px;">
                          <v-text-field
                            v-model.number="zusConfigs[zusType.key].maxZleceniowka"
                            type="number"
                            outlined
                            dense
                            hide-details
                            prefix="PLN"
                            :placeholder="getSuggestedZleceniowka(zusType.key).toFixed(2)"
                          ></v-text-field>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-card-text>
            </v-card>
            <!-- Rachunek Configuration Section -->
            <v-card outlined class="mb-4">
              <v-card-title class="subtitle-1">
                <v-icon left small>mdi-receipt-text</v-icon>
                {{ $t('payoutConfig.receiptConfig') }}
              </v-card-title>
              <v-card-text>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="rachunekConfig.itemDescription"
                      :label="$t('payoutConfig.itemDescription')"
                      outlined
                      dense
                      hint="Domyślnie: Najem pojazdu"
                      persistent-hint
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model.number="rachunekConfig.paymentDays"
                      :label="$t('payoutConfig.paymentDays')"
                      type="number"
                      outlined
                      dense
                      min="1"
                      hint="Liczba dni na zapłatę"
                      persistent-hint
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="rachunekConfig.place"
                      :label="$t('payoutConfig.issuePlace')"
                      outlined
                      dense
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-textarea
                      v-model="rachunekConfig.notes"
                      :label="$t('payoutConfig.additionalNotes')"
                      outlined
                      dense
                      rows="2"
                      auto-grow
                    ></v-textarea>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <v-card outlined class="mb-4">
              <v-card-title class="subtitle-1">
                <v-icon left small>mdi-cash-fast</v-icon>
                {{ $t('selfService.config') }}
              </v-card-title>
              <v-card-text>
                <v-row>
                  <v-col cols="12" md="4">
                    <v-switch
                      v-model="selfService.enabled"
                      :label="$t('selfService.enabled')"
                      dense
                      inset
                    />
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-switch
                      v-model="selfService.manualMode"
                      label="Tryb ręczny (księgowość akceptuje, BLIK wysyłany ręcznie)"
                      dense
                      inset
                      hint="Włączone: każde żądanie trafia na listę do akceptacji (próg auto ignorowany), po akceptacji potrącenie + oznaczenie wypłacone, bez automatycznego przelewu."
                      persistent-hint
                    />
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-select
                      v-model="selfService.provider"
                      :items="providerOptions"
                      :label="$t('selfService.provider')"
                      outlined
                      dense
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model.number="selfService.percentLimit"
                      :label="$t('selfService.percentLimit')"
                      type="number"
                      min="0"
                      max="100"
                      outlined
                      dense
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model.number="selfService.autoApproveThreshold"
                      :label="$t('selfService.autoApproveThreshold')"
                      type="number"
                      min="0"
                      step="10"
                      outlined
                      dense
                      hint="≤ tej kwoty: auto, powyżej: admin"
                      persistent-hint
                    />
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-text-field
                      v-model.number="selfService.maxPerDay"
                      :label="$t('selfService.maxPerDay')"
                      type="number"
                      min="1"
                      max="10"
                      outlined
                      dense
                    />
                  </v-col>
                  <v-col cols="6" md="2">
                    <v-text-field
                      v-model.number="selfService.startHour"
                      :label="$t('selfService.startHour')"
                      type="number"
                      min="0"
                      max="23"
                      outlined
                      dense
                    />
                  </v-col>
                  <v-col cols="6" md="2">
                    <v-text-field
                      v-model.number="selfService.endHour"
                      :label="$t('selfService.endHour')"
                      type="number"
                      min="0"
                      max="23"
                      outlined
                      dense
                    />
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <v-card outlined class="mb-4">
              <v-card-title class="subtitle-1">
                <v-icon left>mdi-bank-transfer</v-icon>
                Nazwy koszyków przelewów
              </v-card-title>
              <v-card-text>
                <div class="text-caption grey--text mb-3">
                  Tytuł, jaki pojawi się w przelewie dla każdego koszyka (bez imienia i nazwiska kierowcy).
                </div>
                <v-row dense>
                  <v-col cols="12" md="6">
                    <v-text-field v-model="basketTitles.zleceniowka" label="Zleceniówka (wynagrodzenie)" outlined dense />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field v-model="basketTitles.zwrotKosztow" label="Zwrot kosztów" outlined dense />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field v-model="basketTitles.najem" label="Najem" outlined dense />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field v-model="basketTitles.zaliczki" label="Zaliczki dzienne" outlined dense />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field v-model="basketTitles.komornik" label="Komornik (gdy brak własnego tytułu zajęcia)" outlined dense />
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="saveConfig" :loading="saving">
            <v-icon left>mdi-content-save</v-icon>
            {{ $t('common.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
  </div>
</template>

<script>
import Swal from "sweetalert2";
const axios = require("axios");

const ZUS_TYPES = [
  { key: 'B2B', label: 'B2B' },
  { key: 'NOT_HIRED_L4', label: 'Niezatrudniony z L4' },
  { key: 'NOT_HIRED_NO_L4', label: 'Niezatrudniony bez L4' },
  { key: 'HIRED_BELOW_WITH_L4', label: 'Zatrudniony poniżej z L4' },
  { key: 'HIRED_BELOW_NO_L4', label: 'Zatrudniony poniżej bez L4' },
  { key: 'HIRED_ABOVE', label: 'Zatrudniony powyżej' },
  { key: 'STUDENT', label: 'Student' },
  { key: 'UNDER_26_NO_L4', label: 'Poniżej 26 lat bez L4' },
  { key: 'UNDER_26_L4', label: 'Poniżej 26 lat z L4' },
];

function createEmptyZusConfigs() {
  const configs = {};
  for (const t of ZUS_TYPES) {
    configs[t.key] = { chorobowe: 0, spoleczne: 0, funduszPracy: 0, zdrowotne: 0, maxZleceniowka: 0 };
  }
  return configs;
}

export default {
  data() {
    return {
      valid: true,
      saving: false,
      zusTypes: ZUS_TYPES,
      zusConfigs: createEmptyZusConfigs(),
      config: {
        minSettlementAmount: 201,
        komornikFee: 20,
        officeInvoiceFee: 2,
      },
      rachunekConfig: {
        itemDescription: 'Najem pojazdu',
        paymentDays: 14,
        place: '',
        notes: '',
      },
      selfService: {
        enabled: false,
        manualMode: false,
        percentLimit: 70,
        autoApproveThreshold: 400,
        maxPerDay: 1,
        startHour: 8,
        endHour: 22,
        provider: 'STUB',
      },
      basketTitles: {
        zleceniowka: 'Wynagrodzenie',
        zwrotKosztow: 'Zwrot kosztow',
        najem: 'Najem',
        zaliczki: 'Zaliczka dzienna',
        komornik: 'Zajecie komornicze',
      },
    };
  },
  computed: {
    providerOptions() {
      return [
        { text: this.$t('selfService.providerStub'), value: 'STUB' },
        { text: this.$t('selfService.providerAutopay'), value: 'AUTOPAY' },
        { text: this.$t('selfService.providerTpay'), value: 'TPAY' },
      ];
    },
  },
  async created() {
    await this.loadConfig();
  },
  methods: {
    getZusTotal(key) {
      const c = this.zusConfigs[key];
      return (c.chorobowe || 0) + (c.spoleczne || 0) + (c.funduszPracy || 0) + (c.zdrowotne || 0);
    },
    getSuggestedZleceniowka(key) {
      const suggested = (this.config.minSettlementAmount || 0) - this.getZusTotal(key);
      return suggested > 0 ? suggested : 0;
    },
    async toggleLoading(type) {
      const toggle = type;
      localStorage.setItem("loading", toggle);
      window.dispatchEvent(
        new CustomEvent("toggleLoading", {
          detail: {
            loading: localStorage.getItem("loading"),
            overlay: localStorage.getItem("loading"),
          },
        })
      );
    },
    async loadConfig() {
      await this.toggleLoading(true);
      try {
        const response = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "payout-config",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        const data = response.data.data;

        // Map zusConfigs array to object keyed by zusType (convert cents to PLN)
        const zusConfigs = createEmptyZusConfigs();
        if (data.zusConfigs) {
          for (const zc of data.zusConfigs) {
            if (zusConfigs[zc.zusType]) {
              zusConfigs[zc.zusType] = {
                chorobowe: (zc.chorobowe || 0) / 100,
                spoleczne: (zc.spoleczne || 0) / 100,
                funduszPracy: (zc.funduszPracy || 0) / 100,
                zdrowotne: (zc.zdrowotne || 0) / 100,
                maxZleceniowka: (zc.maxZleceniowka || 0) / 100,
              };
            }
          }
        }
        this.zusConfigs = zusConfigs;

        this.config = {
          minSettlementAmount: (data.minSettlementAmount || 20100) / 100,
          komornikFee: (data.komornikFee ?? 2000) / 100,
          officeInvoiceFee: (data.officeInvoiceFee ?? 200) / 100,
        };

        this.rachunekConfig = {
          itemDescription: data.rachunekItemDescription || 'Najem pojazdu',
          paymentDays: data.rachunekPaymentDays || 14,
          place: data.rachunekPlace || '',
          notes: data.rachunekNotes || '',
        };

        this.selfService = {
          enabled: !!data.selfServiceEnabled,
          manualMode: !!data.selfServiceManualMode,
          percentLimit: data.selfServicePercentLimit ?? 70,
          autoApproveThreshold: (data.autoApproveThresholdCents ?? 40000) / 100,
          maxPerDay: data.selfServiceMaxPerDay ?? 1,
          startHour: data.selfServiceStartHour ?? 8,
          endHour: data.selfServiceEndHour ?? 22,
          provider: data.paymentProvider || 'STUB',
        };

        this.basketTitles = {
          zleceniowka: data.transferTitleZleceniowka || 'Wynagrodzenie',
          zwrotKosztow: data.transferTitleZwrotKosztow || 'Zwrot kosztow',
          najem: data.transferTitleNajem || 'Najem',
          zaliczki: data.transferTitleZaliczki || 'Zaliczka dzienna',
          komornik: data.transferTitleKomornik || 'Zajecie komornicze',
        };
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response?.data?.message?.toString() || "Błąd ładowania konfiguracji", "error");
      }
      await this.toggleLoading(false);
    },
    async saveConfig() {
      this.saving = true;
      try {
        // Build zusConfigs array (convert PLN to cents)
        const zusConfigs = [];
        for (const t of ZUS_TYPES) {
          const c = this.zusConfigs[t.key];
          zusConfigs.push({
            zusType: t.key,
            chorobowe: Math.round((c.chorobowe || 0) * 100),
            spoleczne: Math.round((c.spoleczne || 0) * 100),
            funduszPracy: Math.round((c.funduszPracy || 0) * 100),
            zdrowotne: Math.round((c.zdrowotne || 0) * 100),
            maxZleceniowka: Math.round((c.maxZleceniowka || 0) * 100),
          });
        }

        await axios({
          method: "patch",
          url: process.env.VUE_APP_ROOT_API + "payout-config",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
          data: {
            zusConfigs,
            minSettlementAmount: Math.round(this.config.minSettlementAmount * 100),
            komornikFee: Math.round((this.config.komornikFee || 0) * 100),
            officeInvoiceFee: Math.round((this.config.officeInvoiceFee || 0) * 100),
            rachunekItemDescription: this.rachunekConfig.itemDescription,
            rachunekPaymentDays: this.rachunekConfig.paymentDays,
            rachunekPlace: this.rachunekConfig.place,
            rachunekNotes: this.rachunekConfig.notes,
            selfServiceEnabled: !!this.selfService.enabled,
            selfServiceManualMode: !!this.selfService.manualMode,
            selfServicePercentLimit: this.selfService.percentLimit,
            autoApproveThresholdCents: Math.round((this.selfService.autoApproveThreshold || 0) * 100),
            selfServiceMaxPerDay: this.selfService.maxPerDay,
            selfServiceStartHour: this.selfService.startHour,
            selfServiceEndHour: this.selfService.endHour,
            paymentProvider: this.selfService.provider,
            transferTitleZleceniowka: this.basketTitles.zleceniowka,
            transferTitleZwrotKosztow: this.basketTitles.zwrotKosztow,
            transferTitleNajem: this.basketTitles.najem,
            transferTitleZaliczki: this.basketTitles.zaliczki,
            transferTitleKomornik: this.basketTitles.komornik,
          },
        });
        Swal.fire("Sukces", "Konfiguracja zapisana", "success");
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response?.data?.message?.toString() || "Błąd zapisu", "error");
      }
      this.saving = false;
    },
  },
};
</script>

<style scoped>
.payout-config-page {
  margin: -24px;
  padding: 24px;
  background: #181C28;
  min-height: calc(100vh - 64px);
  color: #E6EAF2;
}
.page-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 20px;
}
.page-eyebrow {
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #5478FF;
  font-weight: 600;
}
.page-title {
  font-size: 28px;
  font-weight: 700;
  margin: 4px 0 0 0;
  color: #FFFFFF;
}
.content-card {
  background: #232838 !important;
  color: #E6EAF2 !important;
  border-radius: 16px !important;
  border: 1px solid #2A3142 !important;
}
.payout-config-page >>> .v-card {
  background: #232838 !important;
  color: #E6EAF2 !important;
  box-shadow: none !important;
}
.payout-config-page >>> .v-card.v-sheet--outlined {
  background: #1F2433 !important;
  border: 1px solid #2A3142 !important;
  border-radius: 12px !important;
}
.payout-config-page >>> .v-card__title {
  color: #E6EAF2 !important;
  border-bottom: 1px solid #2A3142;
  font-size: 14px !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 14px 18px !important;
}
.payout-config-page >>> .v-card__title .v-icon {
  color: #5478FF !important;
}
.payout-config-page >>> .v-card__text {
  padding: 18px !important;
}
.payout-config-page >>> .v-text-field input,
.payout-config-page >>> .v-text-field textarea,
.payout-config-page >>> .v-select__selection {
  color: #E6EAF2 !important;
}
.payout-config-page >>> .v-label {
  color: #94A0B8 !important;
}
.payout-config-page >>> fieldset {
  border-color: #2A3142 !important;
}
.payout-config-page >>> .v-text-field--outlined fieldset {
  border-color: #2A3142 !important;
}
.payout-config-page >>> .v-text-field--outlined.v-input--is-focused fieldset {
  border-color: #5478FF !important;
}
.payout-config-page >>> .v-input__prepend-outer .v-icon,
.payout-config-page >>> .v-input__prepend-inner .v-icon {
  color: #94A0B8 !important;
}
.payout-config-page >>> .v-text-field__prefix {
  color: #94A0B8 !important;
}
.payout-config-page >>> .v-expansion-panels {
  background: transparent !important;
  border-radius: 12px;
  overflow: hidden;
}
.payout-config-page >>> .v-expansion-panel {
  background: #232838 !important;
  color: #E6EAF2 !important;
  border-bottom: 1px solid #2A3142 !important;
}
.payout-config-page >>> .v-expansion-panel:last-child {
  border-bottom: none !important;
}
.payout-config-page >>> .v-expansion-panel::before {
  box-shadow: none !important;
}
.payout-config-page >>> .v-expansion-panel-header {
  color: #E6EAF2 !important;
  font-weight: 600;
  padding: 14px 18px;
}
.payout-config-page >>> .v-expansion-panel-header__icon .v-icon {
  color: #94A0B8 !important;
}
.payout-config-page >>> .v-expansion-panel-content__wrap {
  padding: 0 18px 18px !important;
}
.payout-config-page >>> .v-simple-table {
  background: transparent !important;
}
.payout-config-page >>> .v-simple-table thead th {
  color: #94A0B8 !important;
  background: #1F2433 !important;
  border-bottom-color: #2A3142 !important;
  font-size: 11px !important;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.payout-config-page >>> .v-simple-table tbody td {
  color: #E6EAF2 !important;
  border-bottom-color: #2A3142 !important;
}
.payout-config-page >>> .v-simple-table tbody tr:hover {
  background: rgba(84, 120, 255, 0.05) !important;
}
.payout-config-page >>> .v-divider {
  border-color: #2A3142 !important;
}
.payout-config-page >>> .v-input--switch label {
  color: #E6EAF2 !important;
}
.payout-config-page >>> .v-card__actions {
  border-top: 1px solid #2A3142;
  padding: 14px 18px !important;
  background: #1F2433 !important;
  border-radius: 0 0 16px 16px;
}
.payout-config-page >>> .grey--text {
  color: #94A0B8 !important;
}
.payout-config-page >>> .primary--text {
  color: #5478FF !important;
}
.payout-config-page >>> .font-weight-bold.primary--text {
  color: #FFFFFF !important;
  background: rgba(84, 120, 255, 0.18);
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
}
</style>
